import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const PfForm12Report = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Lists and loading states
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync session changes from storage
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when firm, month, or year changes
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const monthAbbr = monthName ? monthName.substring(0, 3) : "";

        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthAbbr);
        fd.append("PayrollYear", year);

        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetPFForm12/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              parsedData = JSON.parse(jsonString);
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Find Firm Code No. (PF Code)
  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const records = reportData?.Records || reportData?.Employees || [];

  const processedRows = records.map((rec, index) => {
    return {
      sno: index + 1,
      acNo: rec.AcNo || rec.SerialNo || "",
      uan: rec.Uan || rec.UAN || "",
      name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
      fatherName: (rec.FatherName || "").toUpperCase(),
      basic: parseFloat(rec.Basic || rec.BasicWages || rec.Wages || 0),
      paidDays: parseFloat(rec.PaidDays || rec.PaidDay || 0),
      gross: parseFloat(rec.Gross || rec.GrossWages || rec.GrossAmount || 0),
      eligibleSalary: parseFloat(rec.EligibleSalary || rec.EligibleWages || 0),
      employeePf: parseFloat(rec.EmployeePf || rec.PF || 0),
      emplrAc1: parseFloat(rec.EmplrAc1 || rec.EmplrPF || 0),
      emplrAc10: parseFloat(rec.EmplrAc10 || rec.Pension || 0)
    };
  });

  // Search filter
  const filteredRows = processedRows.filter(row => {
    const query = searchTerm.toLowerCase();
    return row.name.includes(query) || 
           row.fatherName.includes(query) || 
           row.uan.includes(query) || 
           row.acNo.includes(query);
  });

  // Totals calculations
  let totalBasic = 0;
  let totalPaidDays = 0;
  let totalGross = 0;
  let totalEligible = 0;
  let totalEmpPf = 0;
  let totalAc1 = 0;
  let totalAc10 = 0;

  filteredRows.forEach(row => {
    totalBasic += row.basic;
    totalPaidDays += row.paidDays;
    totalGross += row.gross;
    totalEligible += row.eligibleSalary;
    totalEmpPf += row.employeePf;
    totalAc1 += row.emplrAc1;
    totalAc10 += row.emplrAc10;
  });

  // ─── COMPUTE SUMMARY DATA ───
  const summary = reportData?.Summary || {
    PfApplicableWages: 0,
    PfApplicableNos: 0,
    PfExemptWages: 0,
    ExemptWages: 0,
    ExemptNos: 0,
    AbsentNos: 0,
    TotalSummaryWages: 0,
    TotalSummaryNos: 0
  };

  const pfApplicableWages = parseFloat(summary.PfApplicableWages || 0);
  const pfApplicableNos = parseInt(summary.PfApplicableNos || 0);
  const pfExemptWages = parseFloat(summary.PfExemptWages || 0);
  const exemptWages = parseFloat(summary.ExemptWages || 0);
  const exemptNos = parseInt(summary.ExemptNos || 0);
  const absentNos = parseInt(summary.AbsentNos || 0);

  const totalSummaryWages = pfApplicableWages + pfExemptWages + exemptWages;
  const totalSummaryNos = pfApplicableNos + exemptNos + absentNos;

  // Contributions formulas
  const empPlusEmplrAc1 = totalEmpPf + totalAc1;
  const pensionAc10 = totalAc10;
  const adminAc2 = Math.max(Math.round(totalEligible * 0.005), 500);
  const edliAc21 = Math.max(Math.round(totalEligible * 0.005), 200);
  const insuranceAc22 = 0;
  const rightTotal = empPlusEmplrAc1 + pensionAc10 + adminAc2 + edliAc21 + insuranceAc22;

  // Excel Export with conjoined structures
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("PF Form 12");

      worksheet.addRow([sessionFirm?.Name?.toUpperCase() || ""]);
      worksheet.addRow([`EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF ${selectedMonth?.toUpperCase()} ${selectedYear}`]);
      worksheet.addRow([`CODE NO. : ${getFirmPfNo()}`, "", "", "", "", "", "", "FORM NO.12"]);
      worksheet.addRow([]);

      const headerRow1 = worksheet.addRow(["Sr. No.", "AC No", "EMPLOYEE NAME", "BASIC", "GROSS WAGES", "EMPLOYEE P.F.", "EMPLOYER'S SHARE", ""]);
      const headerRow2 = worksheet.addRow(["", "UAN", "FATHER NAME", "PAID DAYS", "ELIGIBLE SALARY", "", "A/C.1 P.F.", "A/C.10 PENSION."]);

      [headerRow1, headerRow2].forEach(row => {
        row.font = { bold: true, color: { argb: "FFFFFFFF" } };
        row.eachCell(cell => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF2C3E50" }
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } }
          };
        });
      });

      worksheet.mergeCells("A5:A6");
      worksheet.mergeCells("F5:F6");
      worksheet.mergeCells("G5:H5");

      filteredRows.forEach((row) => {
        worksheet.addRow([
          row.sno,
          row.acNo,
          row.name,
          row.basic,
          row.gross,
          row.employeePf,
          row.emplrAc1,
          row.emplrAc10
        ]);
        worksheet.addRow([
          "",
          row.uan,
          row.fatherName,
          row.paidDays,
          row.eligibleSalary,
          "",
          "",
          ""
        ]);
      });

      const totR1 = worksheet.addRow(["TOTALS", "", "", totalBasic, totalGross, totalEmpPf, totalAc1, totalAc10]);
      totR1.font = { bold: true };
      worksheet.mergeCells(`A${totR1.number}:C${totR1.number}`);
      totR1.getCell(1).alignment = { horizontal: "right" };

      const totR2 = worksheet.addRow(["PAGE TOTALS / PAID DAYS & ELIGIBLE SALARY", "", "", totalPaidDays, totalEligible, "", "", ""]);
      totR2.font = { bold: true, color: { argb: "FF7F8C8D" } };
      worksheet.mergeCells(`A${totR2.number}:C${totR2.number}`);
      totR2.getCell(1).alignment = { horizontal: "right" };

      // Add space
      worksheet.addRow([]);
      worksheet.addRow([]);

      // Write Summary Headers
      worksheet.addRow(["WAGES SUMMARY", "", "", "", "CONTRIBUTIONS SUMMARY", ""]);
      worksheet.addRow(["Category", "Wages", "Count", "", "Account Description", "Amount"]);

      worksheet.addRow(["PF Applicable", Math.round(pfApplicableWages), pfApplicableNos, "", "Employer+ee Share in PF Contribution A/c No. 1", empPlusEmplrAc1]);
      worksheet.addRow(["PF Exempt", Math.round(pfExemptWages), "", "", "Employers shares in pension fund A/C No.10", pensionAc10]);
      worksheet.addRow(["Exempt", Math.round(exemptWages), exemptNos, "", "ADM Charges A/c No. 2", adminAc2]);
      worksheet.addRow(["Absent", 0, absentNos, "", "EDLI A/C No. 21", edliAc21]);
      worksheet.addRow(["Total :", Math.round(totalSummaryWages), totalSummaryNos, "", "Link Insurences Adm. Charges A/c No. 22", insuranceAc22]);
      worksheet.addRow(["", "", "", "", "Total", rightTotal]);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `PF_Form12_Report_${selectedMonth}_${selectedYear}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Excel export failed.");
    }
  };

  // PDF Export
  const exportToPDF = () => {
    if (filteredRows.length === 0) {
      alert("No data available to export.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");

    pdf.setFontSize(10);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 105, 12, { align: "center" });
    pdf.text(`EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF ${selectedMonth?.toUpperCase()} ${selectedYear}`, 105, 17, { align: "center" });

    pdf.setFontSize(8.5);
    pdf.text(`CODE NO. : ${getFirmPfNo()}`, 14, 23);
    pdf.text("FORM NO.12", 180, 23);

    const tableBody = [];
    filteredRows.forEach(row => {
      tableBody.push([
        row.sno,
        row.acNo,
        row.name,
        row.basic.toFixed(2),
        row.gross.toFixed(2),
        row.employeePf,
        row.emplrAc1,
        row.emplrAc10
      ]);
      tableBody.push([
        "",
        { content: row.uan, styles: { textColor: [100, 100, 100] } },
        { content: row.fatherName, styles: { textColor: [120, 120, 120] } },
        row.paidDays.toFixed(2),
        row.eligibleSalary.toFixed(2),
        "",
        "",
        ""
      ]);
    });

    tableBody.push([
      { content: "TOTALS", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
      { content: totalBasic.toFixed(2), styles: { fontStyle: "bold" } },
      { content: totalGross.toFixed(2), styles: { fontStyle: "bold" } },
      { content: totalEmpPf.toString(), styles: { fontStyle: "bold" } },
      { content: totalAc1.toString(), styles: { fontStyle: "bold" } },
      { content: totalAc10.toString(), styles: { fontStyle: "bold" } }
    ]);
    tableBody.push([
      { content: "PAGE TOTALS / PAID DAYS & ELIGIBLE SALARY", colSpan: 3, styles: { halign: "right", fontStyle: "bold", textColor: [120, 120, 120] } },
      { content: totalPaidDays.toFixed(2), styles: { fontStyle: "bold", textColor: [120, 120, 120] } },
      { content: totalEligible.toFixed(2), styles: { fontStyle: "bold", textColor: [120, 120, 120] } },
      "",
      "",
      ""
    ]);

    autoTable(pdf, {
      head: [
        [
          { content: "Sr.\nNo.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
          { content: "AC No", styles: { halign: "center" } },
          { content: "EMPLOYEE NAME", styles: { halign: "left" } },
          { content: "BASIC", styles: { halign: "right" } },
          { content: "GROSS WAGES", styles: { halign: "right" } },
          { content: "EMPLOYEE\nP.F.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
          { content: "EMPLOYER'S SHARE", colSpan: 2, styles: { halign: "center" } }
        ],
        [
          { content: "UAN", styles: { halign: "center" } },
          { content: "FATHER NAME", styles: { halign: "left" } },
          { content: "PAID DAYS", styles: { halign: "right" } },
          { content: "ELIGIBLE SALARY", styles: { halign: "right" } },
          { content: "A/C.1\nP.F.", styles: { halign: "right" } },
          { content: "A/C.10\nPENSION.", styles: { halign: "right" } }
        ]
      ],
      body: tableBody,
      startY: 27,
      styles: {
        fontSize: 7.5,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        lineColor: [0, 0, 0],
        lineWidth: 0.1
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 10 },
        1: { halign: "center", cellWidth: 26 },
        2: { halign: "left", cellWidth: 50 },
        3: { halign: "right", cellWidth: 22 },
        4: { halign: "right", cellWidth: 26 },
        5: { halign: "right", cellWidth: 18 },
        6: { halign: "right", cellWidth: 18 },
        7: { halign: "right", cellWidth: 18 }
      },
      theme: "grid"
    });

    const finalY = pdf.previousAutoTable.finalY || 150;

    // Draw Wages Summary (Left Side)
    autoTable(pdf, {
      head: [["", "WAGES", "NOS"]],
      body: [
        ["PF Applicable", Math.round(pfApplicableWages).toString(), pfApplicableNos.toString()],
        ["PF Exempt", Math.round(pfExemptWages).toString(), ""],
        ["Exempt", Math.round(exemptWages).toString(), exemptNos.toString()],
        ["Absent", "0", absentNos.toString()],
        [{ content: "Total :", styles: { fontStyle: "bold" } }, { content: Math.round(totalSummaryWages).toString(), styles: { fontStyle: "bold" } }, { content: totalSummaryNos.toString(), styles: { fontStyle: "bold" } }]
      ],
      startY: finalY + 10,
      margin: { right: 108, left: 14 },
      styles: { fontSize: 7, cellPadding: 1.5, lineColor: [0,0,0], lineWidth: 0.05 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0,0,0], fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 32 }, 1: { halign: "right", cellWidth: 28 }, 2: { halign: "right", cellWidth: 18 } }
    });

    // Draw Contributions Summary (Right Side)
    autoTable(pdf, {
      body: [
        ["Employer+ee Share in PF Contribution A/c No. 1", empPlusEmplrAc1.toFixed(2)],
        ["Employers shares in pension fund A/C No.10", pensionAc10.toFixed(2)],
        ["ADM Charges A/c No. 2", adminAc2.toFixed(2)],
        ["EDLI A/C No. 21", edliAc21.toFixed(2)],
        ["Link Insurences Adm. Charges A/c No. 22", insuranceAc22.toFixed(2)],
        [{ content: "Total", styles: { fontStyle: "bold" } }, { content: rightTotal.toFixed(2), styles: { fontStyle: "bold" } }]
      ],
      startY: finalY + 10,
      margin: { left: 110, right: 14 },
      styles: { fontSize: 7, cellPadding: 1.5, lineColor: [0,0,0], lineWidth: 0.05 },
      columnStyles: { 0: { cellWidth: 64 }, 1: { halign: "right", cellWidth: 22 } }
    });

    pdf.save(`PF_Form12_Report_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-form12-container, #print-form12-container * {
        visibility: visible !important;
      }
      #print-form12-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 20px;
        background: #fff !important;
        color: #000 !important;
        font-family: 'Courier New', Courier, monospace !important;
      }
      .no-print {
        display: none !important;
      }
      .form12-print-table {
        border-collapse: collapse !important;
        width: 100% !important;
        margin-top: 15px !important;
      }
      .form12-print-table th, .form12-print-table td {
        border: 1px solid #000 !important;
        color: #000 !important;
        padding: 6px 8px !important;
        font-size: 11px !important;
      }
      .form12-print-table th {
        font-weight: bold !important;
        text-align: center !important;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="PF Form 12" motherMenu="Reports" pageContent="Provident Fund Report" />
      </div>

      {/* Filter Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Search by name, father name, UAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** from the filters above or from the **Dashboard Home** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── WEB INTERFACE VIEW ─── */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Provident Fund Report (Form 12)
                  </h5>
                  <small className="text-muted fs-12">
                    Firm PF Code: <strong className="text-primary">{getFirmPfNo()}</strong> &nbsp;|&nbsp; Period: <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="danger" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#E74C3C", border: "none" }} onClick={exportToPDF}>
                    <i className="fa fa-file-pdf-o me-2" />
                    PDF
                  </Button>
                  <Button variant="success" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#27AE60", border: "none" }} onClick={exportToExcel}>
                    <i className="fa fa-file-excel-o me-2" />
                    Excel
                  </Button>
                  <Button variant="primary" size="sm" className="px-3 text-white" style={{ borderRadius: "5px", fontSize: "12px", background: "#2980B9", border: "none" }} onClick={() => window.print()}>
                    <i className="fa fa-print me-2" />
                    Print Form
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading PF Form 12 Dues...</div>
                  </div>
                ) : (
                  <Fragment>
                    <div className="table-responsive">
                      <Table bordered hover striped={false} className="mb-0 align-middle" style={{ fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}>
                        <thead style={{ background: "#F8F9FA" }}>
                          <tr style={{ borderBottom: "2px solid #E9ECEF" }}>
                            <th rowSpan={2} className="text-center fw-bold" style={{ width: "50px", color: "#495057", verticalAlign: "middle" }}>Sr. No.</th>
                            <th className="text-center fw-bold" style={{ width: "160px", color: "#495057" }}>AC No</th>
                            <th className="fw-bold" style={{ color: "#495057" }}>EMPLOYEE NAME</th>
                            <th className="text-end fw-bold" style={{ width: "130px", color: "#495057" }}>BASIC</th>
                            <th className="text-end fw-bold" style={{ width: "130px", color: "#495057" }}>GROSS WAGES</th>
                            <th rowSpan={2} className="text-center fw-bold" style={{ width: "120px", color: "#495057", verticalAlign: "middle" }}>EMPLOYEE P.F.</th>
                            <th colSpan={2} className="text-center fw-bold" style={{ color: "#495057", borderBottom: "1px solid #E9ECEF" }}>EMPLOYER'S SHARE</th>
                          </tr>
                          <tr style={{ background: "#F8F9FA" }}>
                            <th className="text-center fw-semibold text-muted">UAN</th>
                            <th className="fw-semibold text-muted">FATHER NAME</th>
                            <th className="text-end fw-semibold text-muted">PAID DAYS</th>
                            <th className="text-end fw-semibold text-muted">ELIGIBLE SALARY</th>
                            <th className="text-end fw-semibold text-muted" style={{ width: "110px" }}>A/C.1 P.F.</th>
                            <th className="text-end fw-semibold text-muted" style={{ width: "110px" }}>A/C.10 PENSION.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="text-center py-5 text-muted">
                                No employees under PF coverage found.
                              </td>
                            </tr>
                          ) : (
                            filteredRows.map((row) => (
                              <Fragment key={row.sno}>
                                {/* Row 1 */}
                                <tr style={{ borderBottom: "none" }}>
                                  <td rowSpan={2} className="text-center text-secondary bg-light">{row.sno}</td>
                                  <td className="text-center fw-semibold text-dark">{row.acNo}</td>
                                  <td className="fw-bold text-dark">{row.name}</td>
                                  <td className="text-end text-dark">₹{row.basic.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="text-end text-dark">₹{row.gross.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td rowSpan={2} className="text-center text-dark fw-bold" style={{ verticalAlign: "middle" }}>₹{row.employeePf.toLocaleString('en-IN')}</td>
                                  <td className="text-end text-dark fw-semibold">₹{row.emplrAc1.toLocaleString('en-IN')}</td>
                                  <td className="text-end text-dark fw-semibold">₹{row.emplrAc10.toLocaleString('en-IN')}</td>
                                </tr>
                                {/* Row 2 */}
                                <tr style={{ borderTop: "none", borderBottom: "1px solid #DEE2E6" }}>
                                  <td className="text-center text-muted font-monospace fs-11">{row.uan}</td>
                                  <td className="text-secondary fs-12">{row.fatherName}</td>
                                  <td className="text-end text-secondary fs-12">{row.paidDays.toFixed(2)} Days</td>
                                  <td className="text-end text-primary fw-semibold">₹{row.eligibleSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="text-center text-muted">—</td>
                                  <td className="text-center text-muted">—</td>
                                </tr>
                              </Fragment>
                            ))
                          )}

                          {/* Grand Totals */}
                          {filteredRows.length > 0 && (
                            <Fragment>
                              <tr className="bg-light" style={{ borderTop: "2px solid #DEE2E6", fontWeight: "bold" }}>
                                <td colSpan={3} className="text-end text-dark fw-bold pe-4">TOTALS</td>
                                <td className="text-end text-dark fw-bold">₹{totalBasic.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="text-end text-dark fw-bold">₹{totalGross.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="text-center text-dark fw-bold">₹{totalEmpPf.toLocaleString('en-IN')}</td>
                                <td className="text-end text-dark fw-bold">₹{totalAc1.toLocaleString('en-IN')}</td>
                                <td className="text-end text-dark fw-bold">₹{totalAc10.toLocaleString('en-IN')}</td>
                              </tr>
                              <tr className="bg-light" style={{ borderTop: "none", fontWeight: "bold" }}>
                                <td colSpan={3} className="text-end text-muted pe-4">PAGE TOTALS / PAID DAYS & ELIGIBLE SALARY</td>
                                <td className="text-end text-secondary">{totalPaidDays.toFixed(2)} Days</td>
                                <td className="text-end text-primary">₹{totalEligible.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-center text-muted">—</td>
                              </tr>
                            </Fragment>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Summary Panels */}
                    {filteredRows.length > 0 && (
                      <div className="p-4 bg-light border-top">
                        <Row className="g-4">
                          {/* Left summary */}
                          <Col md={6}>
                            <Card className="border border-secondary-subtle">
                              <Card.Body className="p-3">
                                <Table size="sm" borderless className="mb-0 align-middle fs-13">
                                  <thead>
                                    <tr className="border-bottom text-muted">
                                      <th></th>
                                      <th className="text-end fw-bold">WAGES</th>
                                      <th className="text-end fw-bold">NOS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="fw-semibold text-secondary">PF Applicable</td>
                                      <td className="text-end text-dark">{Math.round(pfApplicableWages).toLocaleString('en-IN')}</td>
                                      <td className="text-end text-dark fw-bold">{pfApplicableNos}</td>
                                    </tr>
                                    <tr>
                                      <td className="fw-semibold text-secondary">PF Exempt</td>
                                      <td className="text-end text-dark">{Math.round(pfExemptWages).toLocaleString('en-IN')}</td>
                                      <td className="text-end text-muted">—</td>
                                    </tr>
                                    <tr>
                                      <td className="fw-semibold text-secondary">Exempt</td>
                                      <td className="text-end text-dark">{Math.round(exemptWages).toLocaleString('en-IN')}</td>
                                      <td className="text-end text-dark fw-bold">{exemptNos}</td>
                                    </tr>
                                    <tr>
                                      <td className="fw-semibold text-secondary">Absent</td>
                                      <td className="text-end text-dark">0</td>
                                      <td className="text-end text-dark fw-bold">{absentNos}</td>
                                    </tr>
                                    <tr className="border-top fw-bold text-dark fs-14">
                                      <td>Total :</td>
                                      <td className="text-end text-primary">₹{Math.round(totalSummaryWages).toLocaleString('en-IN')}</td>
                                      <td className="text-end text-primary">{totalSummaryNos}</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </Card.Body>
                            </Card>
                          </Col>

                          {/* Right summary */}
                          <Col md={6}>
                            <Card className="border border-secondary-subtle">
                              <Card.Body className="p-3">
                                <Table size="sm" borderless className="mb-0 align-middle fs-13">
                                  <tbody>
                                    <tr>
                                      <td className="text-secondary fw-semibold">Employer+ee Share in PF Contribution A/c No. 1</td>
                                      <td className="text-end text-dark fw-bold">₹{empPlusEmplrAc1.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-secondary fw-semibold">Employers shares in pension fund A/C No.10</td>
                                      <td className="text-end text-dark fw-bold">₹{pensionAc10.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-secondary fw-semibold">ADM Charges A/c No. 2</td>
                                      <td className="text-end text-dark fw-bold">₹{adminAc2.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-secondary fw-semibold">EDLI A/C No. 21</td>
                                      <td className="text-end text-dark fw-bold">₹{edliAc21.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-secondary fw-semibold">Link Insurences Adm. Charges A/c No. 22</td>
                                      <td className="text-end text-dark fw-bold">₹{insuranceAc22.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                    <tr className="border-top fw-bold text-dark fs-14">
                                      <td className="text-end pe-3">Total</td>
                                      <td className="text-end text-primary">₹{rightTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Fragment>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* ─── PHYSICAL PRINTING VIEW ─── */}
          <div id="print-form12-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", margin: "4px 0" }}>
              EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF &nbsp;
              <span style={{ textDecoration: "underline" }}>{(selectedMonth || "").toUpperCase()} {selectedYear}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "11px", margin: "8px 0" }}>
              <div>CODE NO. : &nbsp;&nbsp;&nbsp;{getFirmPfNo()}</div>
              <div>FORM NO.12</div>
            </div>

            <table className="form12-print-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: "5%" }}>Sr.<br />No.</th>
                  <th style={{ width: "15%" }}>AC No</th>
                  <th style={{ width: "32%", textAlign: "left" }}>EMPLOYEE NAME</th>
                  <th style={{ width: "12%", textAlign: "right" }}>BASIC</th>
                  <th style={{ width: "12%", textAlign: "right" }}>GROSS WAGES</th>
                  <th rowSpan={2} style={{ width: "10%" }}>EMPLOYEE<br />P.F.</th>
                  <th colSpan={2} style={{ width: "14%" }}>EMPLOYER'S SHARE</th>
                </tr>
                <tr>
                  <th>UAN</th>
                  <th style={{ textAlign: "left" }}>FATHER NAME</th>
                  <th style={{ textAlign: "right" }}>PAID DAYS</th>
                  <th style={{ textAlign: "right" }}>ELIGIBLE SALARY</th>
                  <th>A/C.1 P.F.</th>
                  <th>A/C.10 PENSION.</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <Fragment key={row.sno}>
                    <tr>
                      <td rowSpan={2} style={{ textAlign: "center" }}>{row.sno}</td>
                      <td style={{ textAlign: "center" }}>{row.acNo}</td>
                      <td><strong>{row.name}</strong></td>
                      <td style={{ textAlign: "right" }}>{row.basic.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>{row.gross.toFixed(2)}</td>
                      <td rowSpan={2} style={{ textAlign: "right", fontWeight: "bold" }}>{row.employeePf}</td>
                      <td style={{ textAlign: "right" }}>{row.emplrAc1}</td>
                      <td style={{ textAlign: "right" }}>{row.emplrAc10}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center", fontSize: "9px", color: "#555" }}>{row.uan}</td>
                      <td style={{ fontSize: "10px", color: "#555" }}>{row.fatherName}</td>
                      <td style={{ textAlign: "right" }}>{row.paidDays.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>{row.eligibleSalary.toFixed(2)}</td>
                      <td style={{ textAlign: "center" }}>—</td>
                      <td style={{ textAlign: "center" }}>—</td>
                    </tr>
                  </Fragment>
                ))}
                {/* Total R1 */}
                <tr style={{ fontWeight: "bold" }}>
                  <td colSpan={3} style={{ textAlign: "right" }}>TOTALS</td>
                  <td style={{ textAlign: "right" }}>{totalBasic.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>{totalGross.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>{totalEmpPf}</td>
                  <td style={{ textAlign: "right" }}>{totalAc1}</td>
                  <td style={{ textAlign: "right" }}>{totalAc10}</td>
                </tr>
                {/* Total R2 */}
                <tr style={{ fontWeight: "bold", color: "#444" }}>
                  <td colSpan={3} style={{ textAlign: "right" }}>PAGE TOTALS / PAID DAYS & ELIGIBLE SALARY</td>
                  <td style={{ textAlign: "right" }}>{totalPaidDays.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>{totalEligible.toFixed(2)}</td>
                  <td style={{ textAlign: "center" }}>—</td>
                  <td style={{ textAlign: "center" }}>—</td>
                  <td style={{ textAlign: "center" }}>—</td>
                </tr>
              </tbody>
            </table>

            {/* conformed printing summaries */}
            <div style={{ display: "flex", gap: "20px", marginTop: "20px", width: "100%", pageBreakInside: "avoid" }}>
              <div style={{ flex: 1 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #000" }}>
                      <th style={{ textAlign: "left", padding: "4px" }}></th>
                      <th style={{ textAlign: "right", padding: "4px" }}>WAGES</th>
                      <th style={{ textAlign: "right", padding: "4px" }}>NOS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "4px" }}>PF Applicable</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{Math.round(pfApplicableWages)}</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{pfApplicableNos}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>PF Exempt</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{Math.round(pfExemptWages)}</td>
                      <td style={{ textAlign: "right", padding: "4px" }}></td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>Exempt</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{Math.round(exemptWages)}</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{exemptNos}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>Absent</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>0</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{absentNos}</td>
                    </tr>
                    <tr style={{ borderTop: "1px solid #000", fontWeight: "bold" }}>
                      <td style={{ padding: "4px" }}>Total :</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{Math.round(totalSummaryWages)}</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{totalSummaryNos}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ flex: 1 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: "4px" }}>Employer+ee Share in PF Contribution A/c No. 1</td>
                      <td style={{ textAlign: "right", padding: "4px", fontWeight: "bold" }}>{empPlusEmplrAc1.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>Employers shares in pension fund A/C No.10</td>
                      <td style={{ textAlign: "right", padding: "4px", fontWeight: "bold" }}>{pensionAc10.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>ADM Charges A/c No. 2</td>
                      <td style={{ textAlign: "right", padding: "4px", fontWeight: "bold" }}>{adminAc2.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>EDLI A/C No. 21</td>
                      <td style={{ textAlign: "right", padding: "4px", fontWeight: "bold" }}>{edliAc21.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "4px" }}>Link Insurences Adm. Charges A/c No. 22</td>
                      <td style={{ textAlign: "right", padding: "4px", fontWeight: "bold" }}>{insuranceAc22.toFixed(2)}</td>
                    </tr>
                    <tr style={{ borderTop: "1px solid #000", fontWeight: "bold" }}>
                      <td style={{ textAlign: "right", padding: "4px" }}>Total</td>
                      <td style={{ textAlign: "right", padding: "4px" }}>{rightTotal.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default PfForm12Report;
